import Link from "next/link";
import {
  LOCALES,
  LOCALE_LABEL,
  LOCALE_TAG,
  localePath,
  type Locale,
} from "@/lib/i18n";

/**
 * Language switcher for the public pages.
 *
 * ⚠️ IT SWITCHES THE URL, NOT THE TEXT. Each locale has its own address (`/faq`, `/ht/faq`), and
 * these are real `<Link>`s between them. A toggle that swapped strings client-side on ONE url
 * would be simpler and would quietly break the whole point: a crawler would only ever see one
 * language, `hreflang` would have nothing to point at, and the two versions would compete for the
 * same address.
 *
 * `hrefLang` on each link tells a crawler what it will find on the other side, which is the same
 * claim the page's own `alternates.languages` makes — stated in both places because a search
 * engine treats agreement between them as confirmation, and disagreement as a reason to ignore
 * both.
 *
 * `prefetch={false}`: prefetching the whole other-language page for a link almost nobody clicks
 * costs every visitor bandwidth to save one visitor a moment.
 */
export function LanguageSwitcher({
  locale,
  path,
}: {
  /** The locale currently being rendered. */
  locale: Locale;
  /** Unprefixed path of THIS page, e.g. "/faq" — used to build the same page in each locale. */
  path: string;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Language">
      {LOCALES.map((l) => {
        const active = l === locale;
        return (
          <Link
            key={l}
            href={localePath(l, path)}
            hrefLang={LOCALE_TAG[l]}
            prefetch={false}
            aria-current={active ? "true" : undefined}
            className={
              active
                ? "rounded-md px-2 py-1 text-xs font-semibold text-yellow"
                : "rounded-md px-2 py-1 text-xs text-muted transition-colors hover:text-primary"
            }
          >
            {LOCALE_LABEL[l]}
          </Link>
        );
      })}
    </div>
  );
}
